import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, share, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * HTTP Cache Interceptor
 * Caches GET requests for reference data endpoints.
 * Cache TTL: 5 minutes (configurable via CACHE_TTL_MS).
 *
 * Cached endpoints (reference data that rarely changes):
 * - /session
 * - /classes
 * - /teachers
 * - /transport-area
 * - /sub-type (fee sub types)
 * - /subjects
 * - /transport-fee
 * - /holiday
 * - /plan
 * - /roles
 * - /dashboard/summary (invalidated on session switch, see DASHBOARD_SUMMARY_PATTERN)
 *
 * Mutations (POST/PUT/PATCH/DELETE) on a cacheable endpoint invalidate the
 * matching cache entries so subsequent reads see fresh data.
 */

interface CacheEntry {
  response: HttpResponse<unknown>;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
// Tracks in-flight requests; concurrent identical GETs share one HTTP call.
const pending = new Map<string, Observable<HttpEvent<unknown>>>();
const CACHE_TTL_MS = 5 * 60 * 1000;

const CACHEABLE_PATTERNS = [
  /\/session\b/,
  /\/classes\b/,
  /\/teachers\b/,
  /\/transport-area\b/,
  /\/sub-type\b/,
  /\/subjects\b/,
  /\/transport-fee\b/,
  /\/holiday\b/,
  /\/plan\b/,
  /\/roles\b/,
  // Dashboard summary: cached so the splash prefetch and WelcomeComponent's
  // mount call (identical urlWithParams) share one response. Invalidated on
  // session switch via clearCacheForPattern(DASHBOARD_SUMMARY_PATTERN).
  /\/dashboard\/summary\b/,
];

/** Exposed so session-switch flows can drop stale cross-session summary data. */
export const DASHBOARD_SUMMARY_PATTERN = /\/dashboard\/summary\b/;

function isCacheable(url: string): boolean {
  return CACHEABLE_PATTERNS.some((pattern) => pattern.test(url));
}

function isExpired(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp > CACHE_TTL_MS;
}

export function clearCache(): void {
  cache.clear();
  pending.clear();
}

export function clearCacheForPattern(pattern: RegExp): void {
  for (const key of cache.keys()) {
    if (pattern.test(key)) {
      cache.delete(key);
    }
  }
}

export const cacheInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) {
      for (const pattern of CACHEABLE_PATTERNS) {
        if (pattern.test(req.url)) {
          clearCacheForPattern(pattern);
          break;
        }
      }
    }
    return next(req);
  }

  if (!isCacheable(req.url)) {
    return next(req);
  }

  const cacheKey = req.urlWithParams;
  const cached = cache.get(cacheKey);

  if (cached && !isExpired(cached)) {
    return of(cached.response.clone());
  }

  // Deduplicate: if the same GET is already in-flight, share that observable.
  const inflight = pending.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const request$ = next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.status === 200) {
        cache.set(cacheKey, { response: event.clone(), timestamp: Date.now() });
      }
    }),
    finalize(() => pending.delete(cacheKey)),
    share(),
  );

  pending.set(cacheKey, request$);
  return request$;
};
